// The saved_decks row comparisons shared by useSavedDecks' rev-guarded writes
// and the offline deck outbox (src/lib/offline/deckOutbox.ts): one "row holds
// this payload" test, so an online save and a queued offline attempt settle a
// lost response the same way.
import { canonicalCardMeta, parseCardMeta } from './cardMeta'

/** saved_decks.rev as PostgREST returns it (a JSON number for bigint). */
export function revOf(row: { rev?: unknown } | null | undefined): number | undefined {
  const r = row?.rev
  if (typeof r === 'number' && Number.isFinite(r)) return r
  if (typeof r === 'string' && /^\d+$/.test(r)) return Number(r)
  return undefined
}

// JSON equality that ignores key order (jsonb stores keys in its own order).
export function stableJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    return `{${Object.keys(o).filter(k => o[k] !== undefined).sort().map(k => `${JSON.stringify(k)}:${stableJson(o[k])}`).join(',')}}`
  }
  // eslint-disable-next-line i18next/no-literal-string -- JSON text, never shown
  return JSON.stringify(v ?? null) ?? 'null'
}

// Timestamps the database re-formats (and that say nothing about content).
// eslint-disable-next-line i18next/no-literal-string -- column names
const UNCOMPARED_COLUMNS = new Set(['updated_at', 'source_synced_at'])

/**
 * The row holds EXACTLY what a PATCH wrote: every column of its payload
 * (decklist, card_count, commander, partner, card_meta, bracket, engine, the
 * analysis, ownership, link columns...) except timestamps. card_meta by its
 * canonical form, objects key-order independent, null and absent alike. The
 * ONE "already landed" test, for a zero-row miss and for a lost response.
 */
export function rowHoldsPayload(row: Record<string, unknown>, payload: Record<string, unknown>): boolean {
  for (const [col, sent] of Object.entries(payload)) {
    if (UNCOMPARED_COLUMNS.has(col)) continue
    // eslint-disable-next-line i18next/no-literal-string -- a column name
    if (col === 'card_meta') {
      if (canonicalCardMeta(parseCardMeta(row.card_meta ?? null)) !== canonicalCardMeta(parseCardMeta(sent ?? null))) return false
      continue
    }
    if (stableJson(row[col]) !== stableJson(sent)) return false
  }
  return true
}
